import { defer, EMPTY, expand, filter, first, forkJoin, from, map, mergeMap, Observable, of } from "rxjs"
import { OkxRestClient } from "../../../market"
import { Deal, Order, OrderSide, OrderType, Position, PositionSide } from "../../../model/trade"
import { AlgoOrderType, AlgoRecentHistoryRequest, FillsHistoryRequest, HistoricOrder, InstrumentType, OrderFill, OrderHistoryRequest, OrderListItem, AlgoOrderListItem, AlgoLongHistoryRequest } from "libs/okx-api/src"
import { parseFloatSafe, parseIntSafe } from "src/app/core/func"
import { DealsRequestParams, HistoryDataWithDeals } from "../model"
import { BoardId } from "src/app/core/model/common"
import { InstrumentStorageService } from "../../instrument-storage.service"

export class OkxHistoryProvider {

  constructor(private rest: OkxRestClient, private instrumentStorage: InstrumentStorageService) {}

  requestPositions() {
    return forkJoin([
      this.innerRequestPositions('SWAP')
    ]).pipe(
      map(data => data.flat())
    )
  }

  private innerRequestPositions(instType: InstrumentType) {
    return from(this.rest.getPositions({instType})).pipe(
      map(data => data.map(row => {
        const position: Position = {
          createdTimestamp: parseIntSafe(row.cTime),
          updateTimestamp: parseIntSafe(row.uTime),
          market: 'okx',
          board: instType.toLowerCase() as BoardId,
          symbol: row.instId,
          entryPrice: row.avgPx,
          liquidationPrice: row.liqPx,
          size: row.posSide == 'short' ? -Math.abs(parseFloatSafe(row.pos)) : row.pos,
          positionSide: row.posSide == 'long' ? PositionSide.long : row.posSide == 'short' ? PositionSide.short : PositionSide.both,
          positionVolume: row.notionalUsd,
          unrealisedPnl: row.upl,
        }
        return position
      }).filter(position => parseFloatSafe(position.size) != 0))
    )
  }

  requestDeals(params: DealsRequestParams): Observable<HistoryDataWithDeals> {
    return from(this.rest.getFillsHistory(this.prepareDealsRequestParams(params))).pipe(
      mergeMap(data => data.length > 0 ? forkJoin(data.map(row => this.fillDeal(row))) : of([] as Deal[])),
      map(deals => ({deals, fundings: []}))
    )
  }

  requestOrders(params?: DealsRequestParams) {
    return from(this.rest.getOrderHistory(this.prepareOrdersRequestParams(params))).pipe(
      mergeMap(data => data.length > 0 ? forkJoin(data.map(row => this.fillOrder(row))) : of([] as Order[])),
    )
    /*
    return forkJoin([
      this.rest.getOrderHistory(this.prepareOrdersRequestParams(params)),
      this.rest.getAlgoOrderHistory({...this.prepareOrdersRequestParams(params), ordType: 'conditional', state: 'canceled'})
    ]).pipe(
      mergeMap(([data1, data2]) => {
        const orders$ = data1.length > 0 ? forkJoin(data1.map(row => this.fillOrder(row))) : of([] as Order[])
        const algoOrders = data2.map(row => this.fillAlgoOrder(row))
        return orders$.pipe(
          map(orders => orders.concat(algoOrders))
        )
      }),
    )
    */
  }

  requestActiveOrders() {
    return forkJoin([
      this.innerRequestActiveOrders('SWAP'),
      this.innerRequestActiveAlgoOrders('SWAP', 'conditional')
    ]).pipe(
      map(data => data.flat())
    )
  }

  private innerRequestActiveOrders(instType: InstrumentType) {
    const params: OrderHistoryRequest = {
      instType,
      limit: '100',
    }
    const req = defer(() => this.rest.getOrderList(params))
    return req.pipe(
      expand(data => {
        if (data.length >= 100) {
          params.after = data[data.length - 1].ordId
          return req
        }
        return EMPTY
      }),
      mergeMap(data => data.length > 0 ? forkJoin(data.map(row => this.fillOrder(row))) : of([] as Order[])),
    )
  }

  private innerRequestActiveAlgoOrders(instType: InstrumentType, ordType: AlgoOrderType) {
    const params: AlgoRecentHistoryRequest = {
      instType,
      ordType,
      limit: '100',
    }
    const req = defer(() => this.rest.getAlgoOrderList(params))
    return req.pipe(
      expand(data => {
        if (data.length >= 100) {
          params.after = data[data.length - 1].ordId
          return req
        }
        return EMPTY
      }),
      map(data => data.length > 0 ? data.map(row => this.fillAlgoOrder(row)) : [] as Order[]),
    )
  }

  private fillDeal(row: OrderFill) {
    return this.instrumentStorage.listen('okx', 'swap', row.instId).pipe(
      filter(instrument => instrument != undefined),
      first(),
      map(instrument => {
        const deal: Deal = {
          timestamp: parseIntSafe(row.ts),
          market: 'okx',
          board: row.instType.toLowerCase() as BoardId,
          symbol: row.instId,
          price: row.fillPx,
          quantity: row.fillSz,
          volume: parseFloatSafe(row.fillPx) * parseFloatSafe(row.fillSz) * instrument.lotSize,
          fee: parseFloatSafe(row.fee) * -1,
          feeAsset: row.feeCcy,
          side: row.side == 'buy' ? OrderSide.buy : OrderSide.sell,
          dealId: row.tradeId,
          orderId: row.ordId,
          clientOrderId: row.clOrdId,
        }
        return deal
      })
    )
  }

  private fillOrder(row: OrderListItem | HistoricOrder) {
    return this.instrumentStorage.listen('okx', 'swap', row.instId).pipe(
      filter(instrument => instrument != undefined),
      first(),
      map(instrument => {
        const remainingQuantity = parseFloatSafe(row.sz) - parseFloatSafe(row.accFillSz)
        const remainingVolume = parseFloatSafe(row.px) * remainingQuantity * instrument.lotSize

        const order: Order = {
          createdTimestamp: parseIntSafe(row.cTime),
          updateTimestamp: parseIntSafe(row.uTime),
          type: row.ordType == 'limit' ? OrderType.limit : (row.ordType == 'market' ? OrderType.market : OrderType.unknown),
          status: row.state,
          market: 'okx',
          board: row.instType.toLowerCase() as BoardId,
          symbol: row.instId,
          price: row.px,
          quantity: row.sz,
          volume: parseFloatSafe(row.px) * parseFloatSafe(row.sz) * instrument.lotSize,
          remainingQuantity: remainingQuantity,
          remainingVolume: remainingVolume,
          side: row.side == 'buy' ? OrderSide.buy : OrderSide.sell,
          positionSide: row.posSide == 'long' ? PositionSide.long : row.posSide == 'short' ? PositionSide.short : PositionSide.both,
          orderId: row.ordId,
          clientOrderId: row.clOrdId,
          isStop: false,
          isClosePosition: row.reduceOnly === 'true',
        }

        // Для стоп-ордеров используем trigger price
        if (order.isStop && 'slTriggerPx' in row && row.slTriggerPx) {
          order.price = row.slTriggerPx
          order.volume = parseFloatSafe(order.price) * parseFloatSafe(order.quantity) * instrument.lotSize
          order.remainingQuantity = order.quantity
          order.remainingVolume = order.volume
        }

        return order
      })
    )
  }

  private fillAlgoOrder(row: AlgoOrderListItem) {
    const order: Order = {
      createdTimestamp: parseIntSafe(row.cTime),
      updateTimestamp: parseIntSafe(row.cTime),
      type: OrderType.market,
      status: row.state,
      market: 'okx',
      board: row.instType.toLowerCase() as BoardId,
      symbol: row.instId,
      price: row.slTriggerPx,
      quantity: 0,
      volume: 0,
      remainingQuantity: 0,
      remainingVolume: 0,
      side: row.side == 'buy' ? OrderSide.buy : OrderSide.sell,
      positionSide: row.posSide == 'long' ? PositionSide.long : row.posSide == 'short' ? PositionSide.short : PositionSide.both,
      orderId: row.algoId,
      clientOrderId: row.algoClOrdId,
      isStop: true,
      isClosePosition: row.reduceOnly === 'true',
    }

    // Для алго-ордеров используем trigger price если доступен
    if (row.slTriggerPx) {
      order.price = row.slTriggerPx
      order.volume = 0
      order.remainingQuantity = order.quantity
      order.remainingVolume = order.volume
    }

    return order
  }

  private prepareDealsRequestParams(params: DealsRequestParams) {
    const params2: FillsHistoryRequest = {
      instType: 'SWAP'
    }
    if (params.symbol) {
      params2.instId = params.symbol
    }
    params2.limit = params.limit?.toString() ?? '100'
    return params2
  }

  private prepareOrdersRequestParams<T = AlgoLongHistoryRequest | OrderHistoryRequest>(params?: DealsRequestParams): T {
    const params2: AlgoLongHistoryRequest | OrderHistoryRequest = {
      instType: 'SWAP'
    }
    if (params?.symbol) {
      params2.instId = params.symbol
    }
    params2.limit = params?.limit?.toString() ?? '100'
    return params2 as T
  }

}
